import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { postDataAPI, clearStaleSelectedArtistAndCaches } from '../lib/api'
import { getDropdownPositionLeft } from '../lib/dropdownPosition'

type ManagedArtist = {
  _id: string
  id?: string
  name: string
  email: string
  role: string
  profilePicture?: string
  followers?: number
  accountType?: 'artist' | 'promoter'
}

function getInitials(name: string) {
  if (!name) return '??'
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function getRoleDisplay(role: string) {
  return role === 'manager' ? 'Manager' : role === 'support' ? 'Support' : role === 'promoter' ? 'Promoter' : 'Team Member'
}

export default function ArtistSwitcher() {
  const navigate = useNavigate()
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [managedArtists, setManagedArtists] = useState<ManagedArtist[]>([])
  const [currentArtist, setCurrentArtist] = useState<ManagedArtist | null>(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })
  const [promoterArtistAccounts, setPromoterArtistAccounts] = useState<ManagedArtist[]>([])
  const [loadingPromoterArtists, setLoadingPromoterArtists] = useState(false)

  const [user] = useState<any>(() => {
    try {
      const raw = localStorage.getItem('dizeeUser')
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })

  const token = (() => {
    try {
      const t = localStorage.getItem('dizeeToken')
      if (!t) return null
      const parsed = JSON.parse(t)
      return typeof parsed === 'string' ? parsed : parsed
    } catch {
      return localStorage.getItem('dizeeToken')
    }
  })()

  useEffect(() => {
    const selectedArtist = localStorage.getItem('selectedArtist')
    if (selectedArtist) {
      try {
        setCurrentArtist(JSON.parse(selectedArtist))
      } catch {
        // ignore
      }
    }
  }, [])

  useEffect(() => {
    const fetchManaged = async () => {
      if (!user?.email || !token) return
      try {
        const res = await postDataAPI('team/managed-artists', { teamMemberEmail: user.email }, token)
        const data = res?.data?.data ?? res?.data ?? res
        const list = Array.isArray(data) ? data : []
        setManagedArtists(list)
        // If selected artist is no longer in managed list (e.g. removed from team), clear and go to Team Hub
        try {
          const raw = localStorage.getItem('selectedArtist')
          if (raw && list.length >= 0) {
            const selected = JSON.parse(raw)
            const id = selected?._id ?? selected?.id
            const stillManaged = id && list.some((a: ManagedArtist) => (a._id || a.id) === id)
            if (!stillManaged) {
              clearStaleSelectedArtistAndCaches()
              setCurrentArtist(null)
              window.location.href = '/team-hub'
            }
          }
        } catch {
          // ignore
        }
      } catch (e) {
        console.error('Error fetching managed artists:', e)
      }
    }
    fetchManaged()
  }, [user?.email, token])

  useEffect(() => {
    if (currentArtist?.accountType !== 'promoter' || !currentArtist?._id || !token) {
      setPromoterArtistAccounts([])
      return
    }
    setLoadingPromoterArtists(true)
    postDataAPI('team/promoter-artist-accounts', { promoterId: currentArtist._id }, token)
      .then((res) => {
        const raw = res?.data?.data ?? res?.data ?? res
        const list = Array.isArray(raw) ? raw : []
        setPromoterArtistAccounts(list)
      })
      .catch(() => setPromoterArtistAccounts([]))
      .finally(() => setLoadingPromoterArtists(false))
  }, [currentArtist?.accountType, currentArtist?._id, token])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      // Calculate dropdown position when opening (flip above if near bottom)
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect()
        setDropdownPosition(getDropdownPositionLeft(rect, 320))
      }
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  /** Clear all page-level caches so stale data from previous artist doesn't flash. */
  const clearAllPageCaches = () => {
    // Clear scoped localStorage caches (showsPageCache:*, musicPageCatalogCache:*, etc.)
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && (
        key.startsWith('showsPageCache') ||
        key.startsWith('musicPageCatalogCache') ||
        key.startsWith('showsTicketTrackingOverrides') ||
        key.startsWith('pendingBandsintownShows')
      )) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k))

    // Clear all sessionStorage (session caches are scoped by userId already,
    // but clearing ensures a clean slate on switch)
    sessionStorage.clear()
  }

  const handleSwitchTo = (artist: ManagedArtist) => {
    clearAllPageCaches()
    localStorage.setItem('selectedArtist', JSON.stringify(artist))
    setCurrentArtist(artist)
    setIsOpen(false)
    navigate('/')
    window.location.reload()
  }

  const handleReturnToTeamHub = () => {
    clearAllPageCaches()
    localStorage.removeItem('selectedArtist')
    // Full page navigation so all hooks/state reset (same pattern as handleSwitchTo)
    window.location.href = '/team-hub'
  }

  const isTeamMember = user?.professionRole && ['manager', 'support', 'promoter'].includes(user.professionRole)
  const hasSelectedArtist = !!localStorage.getItem('selectedArtist')

  if (!isTeamMember || managedArtists.length === 0) {
    return null
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
      >
        {currentArtist ? (
          <>
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
              style={{ background: 'linear-gradient(45deg, #6366f1, #8b5cf6)' }}
            >
              {currentArtist.profilePicture ? (
                <img
                  src={currentArtist.profilePicture}
                  alt=""
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                getInitials(currentArtist.name)
              )}
            </div>
            <div className="text-left">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {currentArtist.accountType === 'promoter' ? 'Promoter' : 'Managing'}
              </div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {currentArtist.name}
              </div>
            </div>
          </>
        ) : (
          <div className="text-sm font-medium text-gray-900 dark:text-white">Team Hub</div>
        )}
        <svg
          className={`w-3 h-3 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setIsOpen(false)} />
          <div 
            ref={dropdownRef}
            className="fixed w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-40"
            style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }}
          >
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Your Teams
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
              {managedArtists.map((artist) => (
                <button
                  key={artist._id}
                  type="button"
                  onClick={() => handleSwitchTo(artist)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                    style={{ background: 'linear-gradient(45deg, #6366f1, #8b5cf6)' }}
                  >
                    {artist.profilePicture ? (
                      <img
                        src={artist.profilePicture}
                        alt=""
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      getInitials(artist.name)
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {artist.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      As {getRoleDisplay(artist.role)}
                    </div>
                  </div>
                  {currentArtist?._id === artist._id && (
                    <svg className="w-4 h-4 text-gray-900 dark:text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
            {currentArtist?.accountType === 'promoter' && promoterArtistAccounts.length > 0 && (
              <div className="border-t border-gray-100 dark:border-gray-700">
                <div className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Artist accounts
                </div>
                {loadingPromoterArtists ? (
                  <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">Loading…</div>
                ) : (
                  promoterArtistAccounts.map((artist) => (
                    <button
                      key={artist._id}
                      type="button"
                      onClick={() => handleSwitchTo(artist)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
                    >
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                        style={{ background: 'linear-gradient(45deg, #10b981, #059669)' }}
                      >
                        {artist.profilePicture ? (
                          <img
                            src={artist.profilePicture}
                            alt=""
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          getInitials(artist.name)
                        )}
                      </div>
                      <div className="flex-1 min-w-0 text-sm font-medium text-gray-900 dark:text-white truncate">
                        {artist.name}
                      </div>
                      {currentArtist?._id === artist._id && (
                        <svg className="w-4 h-4 text-gray-900 dark:text-white flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  ))
                )}
              </div>
            )}
            {hasSelectedArtist && currentArtist && (
              <div className="border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleReturnToTeamHub}
                  className="w-full px-4 py-3 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-center"
                >
                  Exit {currentArtist.name}&apos;s account
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
